/**
 * Página de éxito tras checkout Stripe.
 * Hace polling de status para confirmar el pago y activar la suscripción.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 30;          // 30 intentos
const POLL_INTERVAL_START = 1500; // primer intento a 1.5s
const POLL_INTERVAL_MAX = 5000;   // máximo 5s entre intentos

const PagoExitoPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'checking', attempts: 0, data: null });
  const sessionId = params.get('session_id');
  const pollingRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      Promise.resolve().then(() =>
        setState({ status: 'error', attempts: 0, data: { error: 'No se recibió session_id' } })
      );
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/subscriptions/status/${sessionId}`);
        const data = r.data;
        if (data.payment_status === 'paid') {
          setState({ status: 'success', attempts, data });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', attempts, data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: 'timeout', attempts, data });
          return;
        }
        setState({ status: 'pending', attempts, data });
        // intervalo creciente: 1.5s, 2s, 2.5s, 3s, ... capped a 5s
        const nextDelay = Math.min(POLL_INTERVAL_START + attempts * 500, POLL_INTERVAL_MAX);
        pollingRef.current = setTimeout(poll, nextDelay);
      } catch (e) {
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: 'error', attempts, data: { error: e.message } });
          return;
        }
        const nextDelay = Math.min(POLL_INTERVAL_START + attempts * 500, POLL_INTERVAL_MAX);
        pollingRef.current = setTimeout(poll, nextDelay);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="pago-exito-page">
        <CardContent className="p-8 text-center">
          {state.status === 'checking' || state.status === 'pending' ? (
            <>
              <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Procesando tu pago...</h1>
              <p className="text-sm text-muted-foreground">
                Estamos confirmando el pago con Stripe. Intento {state.attempts} de {MAX_ATTEMPTS}.
              </p>
            </>
          ) : state.status === 'success' ? (
            <>
              <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-foreground mb-2">¡Bienvenido!</h1>
              <p className="text-muted-foreground mb-6">
                Tu suscripción <strong>{state.data?.subscription?.tier_name || 'Pro'}</strong> está activa.
              </p>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => navigate('/portal')} data-testid="btn-go-portal">
                  Ir a mi entrenamiento
                </Button>
                {state.data?.subscription?.tier_key?.startsWith('client_family') && (
                  <Button variant="outline" className="w-full" onClick={() => navigate('/portal/familia')}>
                    Invitar a mi familia
                  </Button>
                )}
              </div>
            </>
          ) : state.status === 'expired' ? (
            <>
              <Clock className="h-16 w-16 text-amber-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Sesión expirada</h1>
              <p className="text-muted-foreground mb-6">
                La sesión de pago ha caducado. Vuelve a intentarlo desde la página de precios.
              </p>
              <Button onClick={() => navigate('/pricing')}>Ver planes</Button>
            </>
          ) : state.status === 'timeout' ? (
            <>
              <Clock className="h-16 w-16 text-amber-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">Pago en proceso</h1>
              <p className="text-muted-foreground mb-2">
                El pago se está confirmando con Stripe (puede tardar un par de minutos).
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Tu cuenta ya está creada. Te enviaremos un email cuando se active la suscripción.
              </p>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => navigate('/portal')} data-testid="btn-go-portal-anyway">
                  Ir a mi portal igualmente
                </Button>
                <Button variant="outline" className="w-full" onClick={() => window.location.reload()}>
                  Reintentar verificación
                </Button>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">No pudimos confirmar el pago</h1>
              <p className="text-muted-foreground mb-6">
                Si el cobro se ha realizado, se reflejará en tu cuenta en unos minutos. Si no, contacta con soporte.
              </p>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => navigate('/portal')}>
                  Ir a mi portal igualmente
                </Button>
                <Button variant="outline" className="w-full" onClick={() => navigate('/pricing')}>
                  Volver a planes
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PagoExitoPage;
